import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, CheckCircle, Filter, Globe, Shield, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function TedProcurementAlerts() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ✅ Single SEOHead (with alternates) */}
      <SEOHead
        title="TED Procurement Alerts"
        description="Get alerts for EU tenders published on TED. Monitor new opportunities by CPV, country, procedure type, and estimated value with TED Monitor."
        path="/ted-procurement-alerts"
        alternates={[
          { lang: "en", href: "https://tedmonitor.eu/ted-procurement-alerts" },
          { lang: "it", href: "https://tedmonitor.eu/it/alert-bandi-ted" },
          { lang: "x-default", href: "https://tedmonitor.eu/ted-procurement-alerts" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              TED Procurement Alerts for European Public Tenders
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-3">
              In European public procurement, speed matters. With TED Monitor you can receive automated tender alerts
              for new opportunities published on TED (Tenders Electronic Daily), filtered by your criteria.
            </p>

            <p className="text-base font-medium text-foreground">No manual checking. No missed opportunities.</p>

            {/* Internal links block (SEO + crawling) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/eu-tender-monitoring"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                EU tender monitoring
              </Link>
              <Link
                to="/european-public-procurement-software"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                European public procurement software
              </Link>
              <Link
                to="/multilingual-access"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Multilingual access (24 EU languages)
              </Link>
              <Link
                to="/compliance-and-security"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Compliance and security
              </Link>
              <Link
                to="/pricing"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Pricing
              </Link>
            </div>
          </div>
        </section>

        {/* What Are TED Procurement Alerts */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Bell className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">What are TED procurement alerts</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED procurement alerts are automated notifications that inform you when new public tenders are published
              in the official EU database. Alerts help you act quickly and keep your tender pipeline structured.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Alerts help you:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Capture new opportunities as soon as they are published",
                "React faster and prepare bids earlier",
                "Organize commercial and bid work with a repeatable workflow",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Official Alerts from TED */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Official tender data from Tenders Electronic Daily (TED)
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              All alerts in TED Monitor are based on official data published via{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Tenders Electronic Daily (TED)
              </a>
              .
            </p>

            <p className="text-muted-foreground mb-3 text-sm">This helps ensure:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {["Data reliability", "Complete procurement information", "Consistent EU-wide coverage"].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              You are not relying on partial feeds or non-authoritative sources.
            </p>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Learn more about our data source →
              </Link>
            </p>
          </div>
        </section>

        {/* Customize Your Tender Alerts */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Customize your tender alerts</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Every organization has different procurement targets. TED Monitor lets you tailor alerts to your
              monitoring scope so you only receive relevant tenders.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">You can build alerts based on:</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {["CPV codes", "EU countries", "Procedure type", "Estimated value", "Industry scope"].map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              This reduces noise and keeps your monitoring precise.
            </p>

            <p className="mt-4">
              <Link to="/eu-tender-monitoring" className="text-primary hover:underline font-medium text-sm">
                Explore EU tender monitoring →
              </Link>
            </p>
          </div>
        </section>

        {/* Real-Time Notifications */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Real-time notifications for new EU tenders
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Alerts are generated in line with official TED publications so you can identify opportunities early and
              plan your bid activity in advance.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Operational benefits:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "No informational delay in your monitoring flow",
                "Better bid planning and prioritization",
                "Higher chance to participate in relevant tenders",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              This is especially valuable in competitive procurement categories.
            </p>
          </div>
        </section>

        {/* Who Benefits */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Who benefits from TED procurement alerts
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED procurement alerts are used by organizations that need a structured tender pipeline across Europe.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {[
                "Companies bidding on EU tenders",
                "Consultants and specialized firms",
                "International operators",
                "SMEs entering the EU market",
              ].map((item) => (
                <div key={item} className="p-2 bg-muted/50 rounded-lg border text-center">
                  <p className="font-medium text-foreground text-xs">{item}</p>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Alerts make the market more accessible and significantly reduce procurement discovery time.
            </p>
          </div>
        </section>

        {/* Why Use TED Monitor */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Why use TED Monitor for procurement alerts
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Compared to manual monitoring or fragmented solutions, TED Monitor provides a centralized workflow for
              building and maintaining your tender pipeline.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Simple alert configuration",
                "Advanced filtering criteria",
                "Structured and comparable tender data",
                "Centralized EU market visibility",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Learn more about trust and platform guarantees:
            </p>

            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <Link to="/multilingual-access" className="text-primary hover:underline font-medium">
                Multilingual access →
              </Link>
              <Link to="/compliance-and-security" className="text-primary hover:underline font-medium">
                Compliance and security →
              </Link>
              <Link to="/european-public-procurement-software" className="text-primary hover:underline font-medium">
                Procurement software overview →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Start receiving TED procurement alerts</h2>
            <p className="text-base mb-6 opacity-90">
              If you want to discover EU tender opportunities systematically, tender alerts are essential.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/pricing" className="gap-2">
                  View pricing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Get started</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
                Pricing
              </Link>
              <Link to="/eu-tender-monitoring" className="text-muted-foreground hover:text-foreground">
                EU Tender Monitoring
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Data Source
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Terms
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
